import { useState } from 'react';
import { createRoute, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ROTAS, type TipoAtividade } from '@remoto/shared';
import { rotaPainel } from './painel';
import { chamar } from '../api';
import { usarSessao } from '../estado/sessao';
import { BotaoLeve, Cartao } from '../componentes/layout';
import { CampoDeEscolha } from '../componentes/campos';
import { type OpcaoDoSeletor } from '../componentes/Seletor';
import { Tela } from '../workspace/Tela';

interface ContaNaLista {
  id: string;
  nome: string;
  email: string;
}

interface AtividadeNaLista {
  id: string;
  usuarioId: string;
  sessaoId: string | null;
  maquinaId: string | null;
  tipo: TipoAtividade;
  texto: string | null;
  detalhes: unknown;
  criadoEm: string;
}

interface EventoNaLista {
  id: string;
  usuarioId: string | null;
  maquinaId: string | null;
  acao: string;
  alvo: string | null;
  detalhes: unknown;
  ip: string | null;
  criadoEm: string;
}

interface Pagina<T> {
  total: number;
  pagina: number;
  porPagina: number;
  itens: T[];
}

const ROTULO_TIPO: Record<TipoAtividade, string> = {
  PROMPT: 'prompt',
  CRIACAO_SESSAO: 'sessão criada',
  DECISAO_PERMISSAO: 'permissão decidida',
};

const TIPOS: OpcaoDoSeletor<TipoAtividade | 'todos'>[] = [
  { id: 'todos', rotulo: 'todos os tipos' },
  { id: 'PROMPT', rotulo: 'prompt' },
  { id: 'CRIACAO_SESSAO', rotulo: 'sessão criada' },
  { id: 'DECISAO_PERMISSAO', rotulo: 'permissão decidida' },
];

type Periodo = 'sempre' | 'dia' | 'semana' | 'mes';

const PERIODOS: OpcaoDoSeletor<Periodo>[] = [
  { id: 'sempre', rotulo: 'qualquer data' },
  { id: 'dia', rotulo: 'últimas 24 horas' },
  { id: 'semana', rotulo: 'últimos 7 dias' },
  { id: 'mes', rotulo: 'últimos 30 dias' },
];

const DIAS_DO_PERIODO: Record<Periodo, number | null> = {
  sempre: null,
  dia: 1,
  semana: 7,
  mes: 30,
};

function desdeDoPeriodo(periodo: Periodo): string | undefined {
  const dias = DIAS_DO_PERIODO[periodo];
  if (dias === null) return undefined;
  return new Date(Date.now() - dias * 24 * 60 * 60_000).toISOString();
}

function dataCurta(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Texto truncado com expansão própria — componente nosso, sem glifo unicode:
 * o botão fala por escrito.
 */
function TextoExpansivel({ texto }: { texto: string }): React.JSX.Element {
  const [aberto, setAberto] = useState(false);
  const curto = texto.length <= 160;
  if (curto) return <span className="whitespace-pre-wrap break-words">{texto}</span>;
  return (
    <span className="min-w-0">
      <span className="whitespace-pre-wrap break-words">
        {aberto ? texto : texto.slice(0, 160) + '…'}
      </span>{' '}
      <button
        type="button"
        onClick={() => setAberto(!aberto)}
        className="text-xs text-zinc-500 underline-offset-2 hover:underline"
      >
        {aberto ? 'mostrar menos' : 'mostrar tudo'}
      </button>
    </span>
  );
}

function Paginacao({
  pagina,
  total,
  porPagina,
  aoIr,
}: {
  pagina: number;
  total: number;
  porPagina: number;
  aoIr: (pagina: number) => void;
}): React.JSX.Element {
  const ultima = Math.max(1, Math.ceil(total / porPagina));
  return (
    <div className="flex items-center justify-between pt-3 text-sm text-zinc-400">
      <BotaoLeve onClick={() => aoIr(pagina - 1)} disabled={pagina <= 1}>
        Anterior
      </BotaoLeve>
      <span>
        página {pagina} de {ultima} · {total} registro{total === 1 ? '' : 's'}
      </span>
      <BotaoLeve onClick={() => aoIr(pagina + 1)} disabled={pagina >= ultima}>
        Próxima
      </BotaoLeve>
    </div>
  );
}

function Atividade(): React.JSX.Element {
  const [aba, setAba] = useState<'atividade' | 'auditoria'>('atividade');
  const [usuarioId, setUsuarioId] = useState<string>('todos');
  const [tipo, setTipo] = useState<TipoAtividade | 'todos'>('todos');
  const [periodo, setPeriodo] = useState<Periodo>('sempre');
  const [pagina, setPagina] = useState(1);

  const { data: contas = [] } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => chamar<ContaNaLista[]>(ROTAS.usuarios),
  });

  const opcoesDeConta: OpcaoDoSeletor<string>[] = [
    { id: 'todos', rotulo: 'todas as pessoas' },
    ...contas.map((c) => ({ id: c.id, rotulo: c.nome, descricao: c.email })),
  ];

  const filtros = new URLSearchParams({ pagina: String(pagina), porPagina: '20' });
  if (usuarioId !== 'todos') filtros.set('usuarioId', usuarioId);
  if (tipo !== 'todos' && aba === 'atividade') filtros.set('tipo', tipo);
  const desde = desdeDoPeriodo(periodo);
  if (desde) filtros.set('desde', desde);

  const atividades = useQuery({
    queryKey: ['atividades', filtros.toString()],
    queryFn: () => chamar<Pagina<AtividadeNaLista>>(`${ROTAS.admin.atividades}?${filtros}`),
    enabled: aba === 'atividade',
  });

  const auditoria = useQuery({
    queryKey: ['auditoria', filtros.toString()],
    queryFn: () => chamar<Pagina<EventoNaLista>>(`${ROTAS.admin.auditoria}?${filtros}`),
    enabled: aba === 'auditoria',
  });

  // Conta removida: o id continua na trilha, e a tela diz isso por extenso.
  function nomeDaConta(id: string | null): string {
    if (!id) return 'sistema';
    const conta = contas.find((c) => c.id === id);
    return conta ? conta.nome : `${id.slice(0, 8)}… (conta removida)`;
  }

  function trocarAba(nova: 'atividade' | 'auditoria'): void {
    setAba(nova);
    setPagina(1);
  }

  const botaoDeAba = (id: 'atividade' | 'auditoria', rotulo: string) => (
    <button
      type="button"
      onClick={() => trocarAba(id)}
      aria-pressed={aba === id}
      className={
        'rounded-md px-3 py-1.5 text-sm transition ' +
        (aba === id
          ? 'bg-zinc-100 font-medium text-zinc-900'
          : 'text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200')
      }
    >
      {rotulo}
    </button>
  );

  return (
    <Tela>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-lg border border-zinc-800 bg-zinc-900/60 p-1">
          {botaoDeAba('atividade', 'Atividade')}
          {botaoDeAba('auditoria', 'Auditoria')}
        </div>
        <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
          <CampoDeEscolha<string>
            rotulo="Filtrar por pessoa"
            rotuloOculto
            compacto
            valor={usuarioId}
            opcoes={opcoesDeConta}
            aoEscolher={(id) => {
              setUsuarioId(id);
              setPagina(1);
            }}
          />
          {aba === 'atividade' ? (
            <CampoDeEscolha<TipoAtividade | 'todos'>
              rotulo="Filtrar por tipo"
              rotuloOculto
              compacto
              valor={tipo}
              opcoes={TIPOS}
              aoEscolher={(t) => {
                setTipo(t);
                setPagina(1);
              }}
            />
          ) : null}
          <CampoDeEscolha<Periodo>
            rotulo="Filtrar por período"
            rotuloOculto
            compacto
            valor={periodo}
            opcoes={PERIODOS}
            aoEscolher={(p) => {
              setPeriodo(p);
              setPagina(1);
            }}
          />
        </div>
      </div>

      {aba === 'atividade' ? (
        <Cartao titulo="O que cada pessoa fez">
          {atividades.isLoading ? (
            <p className="text-sm text-zinc-500">Carregando…</p>
          ) : (atividades.data?.itens.length ?? 0) === 0 ? (
            <p className="text-sm text-zinc-500">Nenhuma atividade com estes filtros.</p>
          ) : (
            <ul className="divide-y divide-zinc-800">
              {atividades.data?.itens.map((a) => (
                <li key={a.id} className="space-y-1 py-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                    <span className="text-zinc-400">{dataCurta(a.criadoEm)}</span>
                    <span className="font-medium text-zinc-200">{nomeDaConta(a.usuarioId)}</span>
                    <span className="rounded bg-zinc-800 px-1.5 py-0.5">{ROTULO_TIPO[a.tipo]}</span>
                    {a.sessaoId ? <span>sessão {a.sessaoId.slice(0, 8)}…</span> : null}
                    {a.maquinaId ? <span>máquina {a.maquinaId.slice(0, 8)}…</span> : null}
                  </div>
                  {a.texto ? (
                    <p className="text-zinc-300">
                      <TextoExpansivel texto={a.texto} />
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {atividades.data ? (
            <Paginacao
              pagina={atividades.data.pagina}
              total={atividades.data.total}
              porPagina={atividades.data.porPagina}
              aoIr={setPagina}
            />
          ) : null}
        </Cartao>
      ) : (
        <Cartao titulo="Trilha de auditoria">
          {auditoria.isLoading ? (
            <p className="text-sm text-zinc-500">Carregando…</p>
          ) : (auditoria.data?.itens.length ?? 0) === 0 ? (
            <p className="text-sm text-zinc-500">Nenhum evento com estes filtros.</p>
          ) : (
            <ul className="divide-y divide-zinc-800">
              {auditoria.data?.itens.map((e) => (
                <li key={e.id} className="space-y-1 py-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                    <span className="text-zinc-400">{dataCurta(e.criadoEm)}</span>
                    <span className="font-medium text-zinc-200">{nomeDaConta(e.usuarioId)}</span>
                    <span className="rounded bg-zinc-800 px-1.5 py-0.5">{e.acao}</span>
                    {e.alvo ? <span>alvo {e.alvo.slice(0, 12)}…</span> : null}
                    {e.ip ? <span>{e.ip}</span> : null}
                  </div>
                  {e.detalhes ? (
                    <p className="text-zinc-400">
                      <TextoExpansivel texto={JSON.stringify(e.detalhes)} />
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {auditoria.data ? (
            <Paginacao
              pagina={auditoria.data.pagina}
              total={auditoria.data.total}
              porPagina={auditoria.data.porPagina}
              aoIr={setPagina}
            />
          ) : null}
        </Cartao>
      )}
    </Tela>
  );
}

export const rotaAtividade = createRoute({
  getParentRoute: () => rotaPainel,
  path: '/atividade',
  beforeLoad: async () => {
    // A API barra de novo; aqui é só para não abrir uma tela que vai falhar.
    if (usarSessao.getState().usuario?.papel !== 'ADMIN') throw redirect({ to: '/maquinas' });
  },
  component: Atividade,
});
